import { queryOptions } from "@tanstack/react-query";

import type {
  ApplicationWithApplicant,
  ApplicationWithCase,
  UseCaseDetail,
  UseCaseListResponse,
  UseCaseSummary,
} from "@kansai-ai-hub/api-client";
import type { UseCaseListQuery } from "@kansai-ai-hub/schema";

import { apiClient } from "@/lib/api-client";
import { fetchAllPaginated } from "@/lib/fetch-all-pages";

export const caseKeys = {
  all: ["use-cases"] as const,
  lists: () => [...caseKeys.all, "list"] as const,
  list: (filters: UseCaseListQuery) => [...caseKeys.lists(), filters] as const,
  details: () => [...caseKeys.all, "detail"] as const,
  detail: (id: string) => [...caseKeys.details(), id] as const,
  applications: (id: string) => [...caseKeys.detail(id), "applications"] as const,
  myApplicationForCase: (id: string) =>
    [...caseKeys.detail(id), "my-application"] as const,
} as const;

/**
 * route search から view 等サーバーへ送らない UI 専用パラメータを除外する。
 * そのまま casesListQueryOptions に渡すと queryKey に混入し、
 * ビュー切り替えのたびに別キャッシュ扱いになるため。
 */
export function toCaseListFilters({
  view: _view,
  applied: _applied,
  ...filters
}: UseCaseListQuery & { view?: string; applied?: string }): UseCaseListQuery {
  return filters;
}

export function casesListQueryOptions(filters: UseCaseListQuery) {
  return queryOptions({
    queryKey: caseKeys.list(filters),
    queryFn: () =>
      apiClient.get<UseCaseListResponse>("/api/v1/use-cases", {
        query: {
          q: filters.q,
          industry: filters.industry,
          technology: filters.technology,
          budget: filters.budget,
          status: filters.status,
          statuses: filters.statuses,
          industries: filters.industries,
          technologies: filters.technologies,
          budgets: filters.budgets,
          organizationNames: filters.organizationNames,
          page: filters.page,
          pageSize: filters.pageSize,
        },
      }),
    staleTime: 30_000,
    placeholderData: (previous) => previous,
  });
}

/**
 * 案件一覧を**全件**取得する（会員一覧でクライアント側のフィルタ＋ページネーションに使う）。
 *
 * 応募状況フィルタ（未応募のみ 等）は自分の応募一覧と突き合わせるためサーバーでは判定できず、
 * サーバーのページ境界で隠すと「未応募 4 件が 3 件＋1 件にページ分断される」破綻が起きる。
 * そこで（q / 業界 / ステータス等のサーバーフィルタは効かせたまま）全ページを取得し、
 * 応募状況の絞り込みとページ送りはクライアント側で行う。
 * page / pageSize は queryKey・クエリから除外し、ページ移動で再取得しない。
 */
export function casesListAllQueryOptions(filters: UseCaseListQuery) {
  const { page: _page, pageSize: _pageSize, ...rest } = filters;
  return queryOptions({
    queryKey: [...caseKeys.lists(), "all", rest] as const,
    queryFn: () =>
      fetchAllPaginated<UseCaseSummary>("/api/v1/use-cases", {
        q: rest.q,
        industry: rest.industry,
        technology: rest.technology,
        budget: rest.budget,
        status: rest.status,
        statuses: rest.statuses,
        industries: rest.industries,
        technologies: rest.technologies,
        budgets: rest.budgets,
        organizationNames: rest.organizationNames,
      }),
    staleTime: 30_000,
    placeholderData: (previous) => previous,
  });
}

export function caseDetailQueryOptions(id: string) {
  return queryOptions({
    queryKey: caseKeys.detail(id),
    queryFn: () => apiClient.get<UseCaseDetail>(`/api/v1/use-cases/${id}`),
    staleTime: 60_000,
  });
}

export function caseApplicationsQueryOptions(id: string) {
  return queryOptions({
    queryKey: caseKeys.applications(id),
    // 案件詳細の応募者一覧はクライアント側でページネーションするため、
    // サーバの 1 ページ上限(100)に縛られず全件取得する。
    queryFn: () =>
      fetchAllPaginated<ApplicationWithApplicant>(`/api/v1/use-cases/${id}/applications`),
    staleTime: 30_000,
  });
}

export function myApplicationForCaseQueryOptions(caseId: string) {
  return queryOptions({
    queryKey: caseKeys.myApplicationForCase(caseId),
    queryFn: () =>
      apiClient.get<ApplicationWithCase | null>(
        `/api/v1/applications/mine/for-use-case/${caseId}`,
      ),
    // 監査中はキャッシュを使わず必ずネットワークから再取得し、古いキャッシュで
    // 「予想以上に時間がかかっています」が誤って一瞬出るのを防ぐ。
    staleTime: 0,
    refetchOnMount: "always",
    /**
     * AI 監査の確定を待つポーリング。PENDING_AUDIT のあいだのみ。
     * 起点は **auditPendingSince**（現在の監査フェーズ開始時刻）。createdAt を使うと
     * 再応募で createdAt が古いまま → 初回 tick から 30s 間隔に落ちて遅延反映する不具合があった。
     * 経過時間で **backoff**:
     *   - 1 分未満: 2 秒間隔 (通常はここで完了)
     *   - 3 分未満: 5 秒間隔
     *   - 3 分以降: 30 秒間隔で **継続** (sweeper の自動 retry が後で完了させた場合も
     *     最大 30 秒で画面に反映する。完全停止すると「事務局確認中」表示で固まるため継続する)
     * 監査完了 (NEW / AUDIT_BLOCKED) になったら即停止。
     */
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data || data.status !== "PENDING_AUDIT") return false;
      const anchor = data.auditPendingSince ?? data.createdAt;
      const ageMs = Date.now() - new Date(anchor).getTime();
      if (ageMs < 60_000) return 2000;
      if (ageMs < 180_000) return 5000;
      return 30_000;
    },
  });
}
